import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { toastr } from 'react-redux-toastr';
import { reset, SubmissionError } from 'redux-form';
import { getInitialsAvatarDataURI } from '../../app/common/util/initialsAvatar';
import { closeModal } from '../modals/modalActions';

// Helper to clear Firebase IndexedDB if corrupted.
// Only include the auth token DB when includeAuth is true, and make sure
// the Auth SDK is signed out first so the DB is not locked.
const clearFirebaseIndexedDB = async (includeAuth = false) => {
  if (!('indexedDB' in window)) return false;

  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID || 'reactportal-5992d';
  const dbNames = [
    'firebase-heartbeat-database',
    `firestore/[DEFAULT]/${projectId}/main`,
  ];
  if (includeAuth) {
    dbNames.push('firebaseLocalStorageDb');
  }

  const deleteDb = (name) =>
    new Promise((resolve) => {
      try {
        const req = indexedDB.deleteDatabase(name);
        req.onsuccess = () => resolve(true);
        req.onerror = () => resolve(false);
        req.onblocked = () => resolve(false);
      } catch (_) {
        resolve(false);
      }
    });

  await Promise.all(dbNames.map(deleteDb));
  return true;
};

export const login = (creds) => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const email = (creds.email || '').trim().toLowerCase();

    try {
      await firebase.auth().signInWithEmailAndPassword(email, creds.password);
      dispatch(closeModal());
    } catch (error) {
      // Check for IndexedDB-related errors and retry after clearing
      const errorMsg = String(error?.message || '').toLowerCase();
      const isIndexedDBError =
        errorMsg.includes('idbdatabase') ||
        errorMsg.includes('indexeddb') ||
        errorMsg.includes('quotaexceedederror') ||
        errorMsg.includes('internal error') ||
        (errorMsg.includes('cannot read properties of null') &&
          (errorMsg.includes("'dt'") || errorMsg.includes('"dt"'))) ||
        (error?.code === 'auth/internal-error' &&
          (errorMsg.includes('indexeddb') || errorMsg.includes('idbdatabase')));

      if (isIndexedDBError) {
        // Sign out first so the Auth SDK releases its lock on the token DB;
        // otherwise deleting it can leave the DB blocked and the next sign-in
        // fails until application storage is cleared manually.
        try {
          await firebase.auth().signOut();
        } catch (_) {}

        await clearFirebaseIndexedDB(true);

        try {
          await firebase
            .auth()
            .signInWithEmailAndPassword(email, creds.password);
          dispatch(closeModal());
          return;
        } catch (retryError) {
          throw new SubmissionError({
            _error:
              'Login failed. Please clear your browser data and try again.',
          });
        }
      }

      throw new SubmissionError({ _error: 'Login Failed' });
    }
  };
};

export const sendSignInLink = (email) => {
  return async (dispatch, getState, { getFirebase }) => {
    const fb = getFirebase();
    const trimmedEmail = (email || '').trim().toLowerCase();

    try {
      const sendSignInEmail = fb.functions().httpsCallable('sendSignInEmailV2');
      await sendSignInEmail({ email: trimmedEmail });
      window.localStorage.setItem('emailForSignIn', trimmedEmail);
    } catch (error) {
      const message =
        error?.details?.message ||
        error?.message ||
        'Failed to send sign-in link.';
      throw new SubmissionError({
        _error: message.includes('No account found')
          ? 'No account found with that email address.'
          : message,
      });
    }
  };
};

// Helper: migrate existing user doc to a new auth UID
const migrateUserDoc = async (firestore, existingDoc, newUid) => {
  const existingData = existingDoc.data();

  // Copy existing user data to new UID doc
  await firestore
    .collection('users')
    .doc(newUid)
    .set({
      ...existingData,
      previousUid: existingDoc.id,
      uidMigratedAt: new Date(),
    });

  // Copy subcollections (followers)
  try {
    const followersSnap = await firestore
      .collection('users')
      .doc(existingDoc.id)
      .collection('followers')
      .get();
    if (followersSnap.docs.length > 0) {
      const batch = firestore.batch();
      followersSnap.docs.forEach((fDoc) => {
        batch.set(
          firestore
            .collection('users')
            .doc(newUid)
            .collection('followers')
            .doc(fDoc.id),
          fDoc.data(),
        );
      });
      await batch.commit();
    }
  } catch (subErr) {
    console.error('Error copying followers subcollection:', subErr);
  }

  console.log(`Migrated user doc from ${existingDoc.id} to ${newUid}`);
};

export const finishSignInWithEmailLink = () => {
  return async (dispatch, getState, { getFirebase }) => {
    const fb = getFirebase();
    if (!fb.auth().isSignInWithEmailLink(window.location.href)) {
      return { success: false, error: 'Invalid sign-in link.' };
    }
    let email = window.localStorage.getItem('emailForSignIn');
    if (!email) {
      email = window.prompt('Please provide your email for confirmation');
    }
    if (!email) {
      return { success: false, error: 'Email is required.' };
    }
    try {
      const result = await fb
        .auth()
        .signInWithEmailLink(email, window.location.href);
      window.localStorage.removeItem('emailForSignIn');

      // Check if Firestore user doc exists at the new auth UID
      const currentUser = result.user;
      if (currentUser) {
        const firestore = fb.firestore();
        const userDoc = await firestore
          .collection('users')
          .doc(currentUser.uid)
          .get();

        if (!userDoc.exists) {
          // No doc at this UID — look up existing user by email
          const trimmedEmail = (email || '').trim().toLowerCase();
          let existingSnap = await firestore
            .collection('users')
            .where('email', '==', trimmedEmail)
            .limit(1)
            .get();

          // Fallback: case-insensitive search
          if (existingSnap.empty) {
            const allUsersSnap = await firestore.collection('users').get();
            const match = allUsersSnap.docs.find(
              (doc) => doc.data().email?.toLowerCase().trim() === trimmedEmail,
            );
            if (match) {
              existingSnap = { empty: false, docs: [match] };
            }
          }

          if (!existingSnap.empty) {
            await migrateUserDoc(
              firestore,
              existingSnap.docs[0],
              currentUser.uid,
            );
          } else {
            // No existing user found by email — sign out unauthorized user
            await fb.auth().signOut();
            return {
              success: false,
              error:
                'No account found with that email. Please contact your administrator.',
            };
          }
        }
      }

      dispatch(closeModal());
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Sign-in failed.' };
    }
  };
};

export const loginWithGoogle = () => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebaseInstance = getFirebase();
    const provider = new firebase.auth.GoogleAuthProvider();

    try {
      // Use popup for Google Sign-In (works in browsers and iOS PWA)
      await firebaseInstance.auth().signInWithPopup(provider);
      return await validateGoogleUser(dispatch, { getFirebase });
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        return { success: false };
      }

      // For popup blocked, try redirect as fallback
      if (error.code === 'auth/popup-blocked') {
        try {
          await firebaseInstance.auth().signInWithRedirect(provider);
          return { success: true, redirect: true };
        } catch (redirectError) {
          console.error('Redirect failed:', redirectError);
        }
      }

      toastr.error(
        'Error',
        error.message || 'Google sign-in failed. Please try again.',
      );
      return { success: false };
    }
  };
};

// Validate the Google user and swap their throwaway Google Auth session for a
// custom-token session bound to their ORIGINAL UID. This preserves every
// follower/following/session reference that points at the original UID.
const validateGoogleUser = async (dispatch, extras) => {
  const { getFirebase } = extras || {};
  const firebaseInstance = getFirebase ? getFirebase() : firebase;

  try {
    const callable = firebaseInstance
      .functions()
      .httpsCallable('googleSignInCustomTokenV2');
    const result = await callable();
    const data = result?.data || {};

    if (!data.token) {
      throw new Error('No sign-in token returned');
    }

    // Sign in as the original UID — replaces the throwaway Google session
    await firebaseInstance.auth().signInWithCustomToken(data.token);

    dispatch(closeModal());
    return { success: true };
  } catch (error) {
    // Make sure the throwaway Google session doesn't linger
    try {
      await firebaseInstance.auth().signOut();
    } catch (signOutErr) {
      console.error(
        'Could not sign out after failed Google login:',
        signOutErr,
      );
    }

    const code = error?.code || '';
    if (code.includes('not-found')) {
      toastr.error(
        'Access Denied',
        'Your account has not been set up. Please contact your administrator.',
      );
    } else if (code.includes('permission-denied')) {
      toastr.error(
        'Access Denied',
        'Your account is not active. Please contact your administrator.',
      );
    } else {
      console.error('Google sign-in validation failed:', error);
      toastr.error(
        'Error',
        error?.message || 'Google sign-in failed. Please try again.',
      );
    }
    return { success: false };
  }
};

// Completes a Google sign-in that used the redirect fallback (popup blocked).
// Call once on app load; it's a no-op when there's no pending redirect result.
export const completeGoogleRedirectSignIn = () => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebaseInstance = getFirebase();
    try {
      const result = await firebaseInstance.auth().getRedirectResult();
      if (!result || !result.user) {
        return { success: false, noRedirect: true };
      }
      // Just signed in via redirect as a throwaway Google user — swap to the
      // original UID using the custom-token flow.
      return await validateGoogleUser(dispatch, { getFirebase });
    } catch (error) {
      console.error('Google redirect sign-in failed:', error);
      try {
        await firebaseInstance.auth().signOut();
      } catch (signOutErr) {
        console.error('Could not sign out after failed redirect:', signOutErr);
      }
      toastr.error(
        'Error',
        error?.message || 'Google sign-in failed. Please try again.',
      );
      return { success: false };
    }
  };
};

export const createAuthForExistingUser = (uid, email, password) => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    try {
      const func = firebase
        .functions()
        .httpsCallable('createAuthForExistingUser');
      await func({ uid, email, password });
      toastr.success('Success', 'Auth account created for existing user');
      return { success: true };
    } catch (error) {
      console.error('Error creating auth for existing user:', error);
      toastr.error('Error', error.message || 'Failed to create auth account');
      throw error;
    }
  };
};

export const adminSetUserPassword = (userId, newPassword) => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    try {
      if (!userId) {
        toastr.error('Error', 'No user selected');
        return;
      }
      if (typeof newPassword !== 'string' || newPassword.length < 8) {
        toastr.error('Error', 'Password must be at least 8 characters');
        return;
      }

      const setUserPasswordFunc = firebase
        .functions()
        .httpsCallable('setUserPasswordV2');
      await setUserPasswordFunc({ userId, newPassword });
      toastr.success('Success', 'Password updated');
    } catch (error) {
      console.error('Error setting user password:', error);
      if (
        error.code === 'not-found' ||
        error.code === 'internal' ||
        error.message?.toLowerCase().includes('no user record') ||
        error.message?.toLowerCase().includes('not found')
      ) {
        toastr.error(
          'Error',
          'No auth account found for this user. Use "Create Auth Account" first.',
        );
      } else {
        toastr.error('Error', 'Failed to update password');
      }
      throw error;
    }
  };
};

export const adminSetUserEmail = (userId, newEmail) => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    try {
      if (!userId) {
        toastr.error('Error', 'No user selected');
        return { success: false };
      }
      const email = (newEmail || '').trim().toLowerCase();
      if (!email) {
        toastr.error('Error', 'Email is required');
        return { success: false };
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toastr.error('Error', 'Invalid email format');
        return { success: false };
      }

      const setUserEmailFunc = firebase
        .functions()
        .httpsCallable('setUserEmailV2');
      await setUserEmailFunc({ userId, newEmail: email });
      toastr.success('Success', 'Email updated');
      return { success: true, newEmail: email };
    } catch (error) {
      console.error('Error setting user email:', error);
      toastr.error('Error', error.message || 'Failed to update email');
      return { success: false };
    }
  };
};

export const adminSendPasswordResetEmail = (targetEmail) => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    try {
      const email = (targetEmail || '').trim().toLowerCase();
      if (!email) {
        toastr.error('Error', 'No email found for this user');
        return;
      }
      const actionCodeSettings = {
        url: 'https://portal.reactbh.org/login',
        handleCodeInApp: false,
      };
      await firebase.auth().sendPasswordResetEmail(email, actionCodeSettings);
      toastr.success(
        'Password Reset',
        'Password reset email sent. The user should check their email to set a new password.',
      );
    } catch (error) {
      console.error('Error sending password reset email:', error);
      toastr.error('Error', 'Failed to send password reset email');
      throw error;
    }
  };
};

export const resetPassword = (email) => {
  return async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    try {
      const actionCodeSettings = {
        url: 'https://portal.reactbh.org/login',
        handleCodeInApp: false,
      };
      await firebase
        .auth()
        .sendPasswordResetEmail(email.email, actionCodeSettings);
      toastr.message(
        'Password Reset',
        'Password reset submitted. Please check your email to change your password!',
      );
    } catch (err) {
      // You may want to differentiate common errors:
      // auth/user-not-found, auth/invalid-email, etc.
      toastr.message(
        'Password Reset',
        'Password reset submitted. Please check your email to change your password!',
      );
    }
  };
};

export const registerUser =
  (user) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    const firebase = getFirebase();
    try {
      const createUserAccountFunc = firebase
        .functions()
        .httpsCallable('createUserAccountV2');

      const result = await createUserAccountFunc({
        email: (user.email || '').trim().toLowerCase(),
        password: user.password,
        displayName: user.displayName || '',
        role: user.role,
        newHireId: user.newHireId || null,
        initialPm: user.initialPm || '',
        identifier: user.identifier || '',
        gustoEmployeeId: user.gustoEmployeeId || '',
        // Client-specific fields
        dob: user.dob || '',
        clientMedicaidID: user.clientMedicaidID || '',
        addressLine1: user.addressLine1 || '',
        addressLine2: user.addressLine2 || '',
        city: user.city || '',
        phone: user.phone || '',
        initialBhp: user.initialBhp || '',
        emergencyContact: user.emergencyContact || '',
        latitude: user.latitude || '',
        longitude: user.longitude || '',
        zip: user.zip || '',
      });

      if (result.data?.success) {
        toastr.success('Success', 'User account created');
      }

      dispatch(closeModal());
    } catch (error) {
      console.log(error);
      throw new SubmissionError({
        _error: error.message || 'Failed to create user account',
      });
    }
  };

export const manageUser =
  (users) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    const firebase = getFirebase();
    const db = firebase.firestore();

    try {
      console.log('manageUser called with:', users);
      const selectedUserId = users.userId;
      if (!selectedUserId) {
        toastr.error('Error', 'Please select a user.');
        return;
      }
      console.log('selectedUserId:', selectedUserId);

      const selectedUserSnap = await db
        .collection('users')
        .doc(selectedUserId)
        .get();
      if (!selectedUserSnap.exists) {
        toastr.error('Error', 'Primary user not found.');
        return;
      }
      const selectedUser = selectedUserSnap.data();
      const selectedUserRole = selectedUser.role;

      // Which connection collection to read
      const idsSnap =
        selectedUserRole === 'client'
          ? await db.collection(`users/${selectedUserId}/followers`).get()
          : await db.collection(`users/${selectedUserId}/following`).get();

      const followingDocs = (
        await Promise.all(
          idsSnap.docs
            .filter((d) => d.id && d.id.trim() !== '') // Filter out empty IDs
            .map((d) => db.doc(`users/${d.id}`).get()),
        )
      )
        .filter((doc) => doc.exists)
        .map((doc) => ({ id: doc.id, ...doc.data() }));

      const selectedUser2Id = users.userId2;
      console.log('selectedUser2Id:', selectedUser2Id);
      if (!selectedUser2Id) {
        toastr.error('Error', 'Please select a second user.');
        return;
      }

      const selectedUser2Snap = await db
        .collection('users')
        .doc(selectedUser2Id)
        .get();
      if (!selectedUser2Snap.exists) {
        toastr.error('Error', 'Secondary user not found.');
        return;
      }
      const selectedUser2 = selectedUser2Snap.data();

      const isAlreadyLinked = !!followingDocs.find(
        (e) => e.id === selectedUser2Id,
      );
      const linkDocRef = db
        .collection('users')
        .doc(selectedUserId)
        .collection('following')
        .doc(selectedUser2Id);

      if (selectedUserRole !== 'client' && !isAlreadyLinked) {
        await linkDocRef.set({
          displayName: selectedUser2.displayName,
          photoURL:
            selectedUser2.photoURL &&
            selectedUser2.photoURL !== '/assets/user.png'
              ? selectedUser2.photoURL
              : getInitialsAvatarDataURI(selectedUser2.displayName),
          role: selectedUser2.role,
        });
        toastr.success('User successfully updated!');
        return;
      }

      // Otherwise remove
      await linkDocRef.delete();
      toastr.success('User successfully removed!');
    } catch (error) {
      console.error(error);
      throw new SubmissionError({ _error: error.message });
    }
  };

// export const updateEmail = (newEmail) => {
//   return async (dispatch, getState, { getFirebase }) => {
//     const firebase = getFirebase();
//     const user = firebase.auth().currentUser;
//     try {
//       await user.updateEmail(newEmail);
//       toastr.success(
//         'Email Updated',
//         'Your email address has been successfully updated.',
//       );
//     } catch (error) {
//       console.error('Error updating email:', error);
//       toastr.error('Email Update Failed', error.message);
//       throw new SubmissionError({
//         _error: error.message,
//       });
//     }
//   };
// };

export const updatePassword =
  (creds) =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const user = firebase.auth().currentUser;
    try {
      await user.updatePassword(creds.newPassword1);
      await dispatch(reset('account'));
      toastr.success('Success!', 'Your password has been updated');
    } catch (error) {
      if (error.code === 'auth/requires-recent-login') {
        toastr.error(
          'Action requires recent login',
          'Please log in again and retry.',
        );
      }
      throw new SubmissionError({ _error: error.message });
    }
  };

export const toggleClientActive =
  (userId, currentActiveStatus) =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const db = firebase.firestore();

    try {
      const newStatus = currentActiveStatus === 'true' ? 'false' : 'true';
      const disabled = newStatus === 'false';
      await db.collection('users').doc(userId).update({
        active: newStatus,
        // Persist so the staff list can show disabled accounts without
        // a per-user Firebase Auth lookup
        authDisabled: disabled,
      });

      // Also sync Firebase Auth disabled status
      const setUserAuthDisabledFunc = firebase
        .functions()
        .httpsCallable('setUserAuthDisabledV2');
      try {
        await setUserAuthDisabledFunc({ userId, disabled });
      } catch (authError) {
        console.warn(
          'Could not sync Firebase Auth disabled status:',
          authError,
        );
      }

      const statusText = newStatus === 'true' ? 'active' : 'discharged';
      toastr.success('Success', `User status updated to ${statusText}`);
      return newStatus;
    } catch (error) {
      console.error('Error toggling user active status:', error);
      toastr.error('Error', 'Failed to update user status');
      throw error;
    }
  };

// Set client status to 'true' (active), 'false' (discharged), or 'hold'
// 'hold' keeps auth active but excludes from reports
export const setClientStatus =
  (userId, newStatus) =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const db = firebase.firestore();

    try {
      // Persist so the staff list can show disabled accounts without a
      // per-user Firebase Auth lookup ('hold' keeps auth active)
      const updateData = {
        active: newStatus,
        authDisabled: newStatus === 'false',
      };

      // When activating a pending/applicant user, flag them as a new hire
      // so they remain on the new-hire tracker until onboarding is complete.
      if (newStatus === 'true') {
        const userSnap = await db.collection('users').doc(userId).get();
        const prev = userSnap.exists ? userSnap.data().active : undefined;
        if (prev === 'pending' || prev === 'applicant') {
          updateData.isNewHire = true;
        }
      }

      await db.collection('users').doc(userId).update(updateData);

      // Only disable Firebase Auth for 'false' (discharged) status
      // Note: The Cloud Function also updates userIdentifiers.identifier when disabling
      // 'hold' keeps auth active so they can still log in
      const disabled = newStatus === 'false';
      const setUserAuthDisabledFunc = firebase
        .functions()
        .httpsCallable('setUserAuthDisabledV2');
      // Best-effort, fire-and-forget: the Firestore status update above is the
      // source of truth. Don't block on this callable (it can be slow, time
      // out, or be permission-denied for non-admins), which would otherwise
      // leave the status dropdown stuck spinning.
      setUserAuthDisabledFunc({ userId, disabled }).catch((authError) => {
        console.warn(
          'Could not sync Firebase Auth disabled status:',
          authError,
        );
      });

      const statusLabels = {
        true: 'Active',
        false: 'Discharged',
        hold: 'On Hold',
      };
      toastr.success(
        'Success',
        `User status updated to ${statusLabels[newStatus] || newStatus}`,
      );
      return newStatus;
    } catch (error) {
      console.error('Error setting user status:', error);
      toastr.error('Error', 'Failed to update user status');
      throw error;
    }
  };

export const toggleTestUser =
  (userId, currentTestStatus) =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const db = firebase.firestore();

    try {
      const newStatus = currentTestStatus === true ? false : true;
      await db.collection('users').doc(userId).update({
        testUser: newStatus,
      });

      const statusText = newStatus ? 'test user' : 'regular user';
      toastr.success('Success', `User marked as ${statusText}`);
      return newStatus;
    } catch (error) {
      console.error('Error toggling test user status:', error);
      toastr.error('Error', 'Failed to update test user status');
      throw error;
    }
  };

export const removeFollower =
  (userId, followerId, userRole) =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const db = firebase.firestore();

    try {
      // Determine which collection to delete from based on role
      const collectionName = userRole === 'client' ? 'followers' : 'following';

      await db
        .collection('users')
        .doc(userId)
        .collection(collectionName)
        .doc(followerId)
        .delete();

      toastr.success('Success', 'Follower removed successfully');
    } catch (error) {
      console.error('Error removing follower:', error);
      toastr.error('Error', 'Failed to remove follower');
      throw error;
    }
  };

export const getUserAuthStatus =
  (userId) =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const getUserAuthStatusFunc = firebase
      .functions()
      .httpsCallable('getUserAuthStatusV2');

    try {
      const result = await getUserAuthStatusFunc({ userId });
      return result.data;
    } catch (error) {
      console.error('Error fetching auth status:', error);
      console.error(
        'Error code:',
        error.code,
        'Message:',
        error.message,
        'Details:',
        error.details,
      );
      if (
        error.code === 'not-found' ||
        error.code === 'internal' ||
        error.message?.toLowerCase().includes('no user record') ||
        error.message?.toLowerCase().includes('not found') ||
        error.message?.toLowerCase().includes('internal')
      ) {
        return { disabled: false, exists: false };
      }
      return { disabled: false, exists: true };
    }
  };

export const setUserAuthDisabled =
  (userId, disabled) =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const setUserAuthDisabledFunc = firebase
      .functions()
      .httpsCallable('setUserAuthDisabledV2');

    try {
      const result = await setUserAuthDisabledFunc({ userId, disabled });
      // Persist so the staff list can read the flag directly
      try {
        await firebase
          .firestore()
          .collection('users')
          .doc(userId)
          .update({ authDisabled: disabled });
      } catch (fsError) {
        console.warn('Could not persist authDisabled flag:', fsError);
      }
      toastr.success(
        'Success',
        `User ${disabled ? 'disabled' : 'enabled'} successfully`,
      );
      return result.data;
    } catch (error) {
      console.error('Error updating auth status:', error);
      toastr.error('Error', 'Failed to update authentication status');
      throw error;
    }
  };

export const syncAuthToFirestore =
  () =>
  async (dispatch, getState, { getFirebase }) => {
    const firebase = getFirebase();
    const syncAuthToFirestoreFunc = firebase
      .functions()
      .httpsCallable('syncAuthToFirestoreV2');

    try {
      const result = await syncAuthToFirestoreFunc();
      toastr.success(
        'Success',
        `Synced ${result.data.syncedCount} users from Firebase Auth to Firestore`,
      );
      return result.data;
    } catch (error) {
      console.error('Error syncing auth to Firestore:', error);
      toastr.error('Error', 'Failed to sync authentication status');
      throw error;
    }
  };
